import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from '@/components/charts/recharts';
import { TrendingUp, Wallet, PiggyBank, ArrowRight, AlertTriangle, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';

function formatIndianNumber(num) {
  if (num === null || num === undefined || Number.isNaN(num)) return '';
  const numStr = Math.round(num).toString();
  let lastThree = numStr.substring(numStr.length - 3);
  let otherNumbers = numStr.substring(0, numStr.length - 3);
  if (otherNumbers !== '') lastThree = `,${lastThree}`;
  return otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + lastThree;
}

function parseNumber(str) {
  if (str === null || str === undefined) return 0;
  const cleaned = String(str).replace(/,/g, '').trim();
  if (cleaned === '') return NaN;
  const num = Number(cleaned);
  return Number.isFinite(num) ? num : NaN;
}

export default function RetirementCalculator() {
  const [currentAge, setCurrentAge] = useState(30);
  const [retirementAge, setRetirementAge] = useState(60);
  const [lifeExpectancy, setLifeExpectancy] = useState(85);
  const [monthlyExpense, setMonthlyExpense] = useState(50000);
  const [inflationRate, setInflationRate] = useState(6);
  const [expectedReturn, setExpectedReturn] = useState(10);
  const [expectedReturnPostRetirement, setExpectedReturnPostRetirement] = useState(10);
  const [currentSavings, setCurrentSavings] = useState(500000);
  const [monthlySIP, setMonthlySIP] = useState(20000);
  const [enableStepUp, setEnableStepUp] = useState(false);
  const [stepUpType, setStepUpType] = useState('percentage');
  const [stepUpValue, setStepUpValue] = useState(10);

  // Input display states (support clear + Indian formatting for amounts)
  const [currentAgeInput, setCurrentAgeInput] = useState('30');
  const [retirementAgeInput, setRetirementAgeInput] = useState('60');
  const [lifeExpectancyInput, setLifeExpectancyInput] = useState('85');
  const [monthlyExpenseInput, setMonthlyExpenseInput] = useState(formatIndianNumber(50000));
  const [inflationRateInput, setInflationRateInput] = useState('6');
  const [expectedReturnInput, setExpectedReturnInput] = useState('10');
  const [expectedReturnPostRetirementInput, setExpectedReturnPostRetirementInput] = useState('10');
  const [currentSavingsInput, setCurrentSavingsInput] = useState(formatIndianNumber(500000));
  const [monthlySIPInput, setMonthlySIPInput] = useState(formatIndianNumber(20000));
  const [stepUpValueInput, setStepUpValueInput] = useState('10');

  const [tableVisibleRows, setTableVisibleRows] = useState(15);

  const results = useMemo(() => {
    const yearsToRetirement = Math.max(0, retirementAge - currentAge);
    const retirementYears = Math.max(1, lifeExpectancy - retirementAge);
    
    // Calculate inflation-adjusted monthly expense at retirement
    const expenseAtRetirement = monthlyExpense * Math.pow(1 + inflationRate / 100, yearsToRetirement);
    const annualExpenseAtRetirement = expenseAtRetirement * 12;
    
    // Calculate required corpus using annuity formula (considering inflation during retirement)
    // Use a real return rate during retirement (post-retirement return - inflation).
    const realReturnDuringRetirement = Math.max(0.01, (expectedReturnPostRetirement - inflationRate) / 100);
    const requiredCorpus = annualExpenseAtRetirement * (1 - Math.pow(1 + realReturnDuringRetirement, -retirementYears)) / realReturnDuringRetirement;
    
    // Calculate corpus accumulated through current savings + SIP
    const monthlyRate = expectedReturn / 100 / 12;
    const months = yearsToRetirement * 12;
    
    let totalInvestment = currentSavings;
    let accumulatedCorpus = currentSavings;
    let currentMonthlyInvestment = monthlySIP;
    const yearlyData = [];

    for (let month = 1; month <= months; month++) {
      totalInvestment += currentMonthlyInvestment;
      accumulatedCorpus = (accumulatedCorpus + currentMonthlyInvestment) * (1 + monthlyRate);

      // Step-up at the start of each year
      if (enableStepUp && month % 12 === 0 && month < months) {
        if (stepUpType === 'percentage') {
          currentMonthlyInvestment = currentMonthlyInvestment * (1 + stepUpValue / 100);
        } else {
          currentMonthlyInvestment = currentMonthlyInvestment + stepUpValue;
        }
      }

      // Record yearly data
      if (month % 12 === 0) {
        const year = month / 12;
        const age = currentAge + year;
        yearlyData.push({
          year: `Age ${age}`,
          age: age,
          investment: Math.round(totalInvestment),
          corpus: Math.round(accumulatedCorpus),
          utilisedAmount: 0,
          remainingCorpus: Math.round(accumulatedCorpus),
          requiredCorpus: Math.round(requiredCorpus),
        });
      }
    }

    // Post-retirement withdrawal simulation (yearly), for table continuity till life expectancy.
    // Assumes withdrawal during the year and corpus earns post-retirement return on remaining amount.
    const annualReturnPost = expectedReturnPostRetirement / 100;
    let remainingCorpus = accumulatedCorpus;
    const annualExpenseBase = annualExpenseAtRetirement;

    for (let year = 1; year <= retirementYears; year++) {
      const age = retirementAge + year;
      const annualExpense = annualExpenseBase * Math.pow(1 + inflationRate / 100, year - 1);

      const startingCorpus = remainingCorpus;
      const utilised = annualExpense;
      const afterWithdrawal = Math.max(0, startingCorpus - annualExpense);
      remainingCorpus = afterWithdrawal > 0 ? afterWithdrawal * (1 + annualReturnPost) : 0;

      yearlyData.push({
        year: `Age ${age}`,
        age: age,
        investment: Math.round(totalInvestment),
        corpus: Math.round(startingCorpus),
        utilisedAmount: Math.round(utilised),
        remainingCorpus: Math.round(remainingCorpus),
        requiredCorpus: Math.round(requiredCorpus),
      });
    }

    // Calculate shortfall
    const shortfall = Math.max(0, requiredCorpus - accumulatedCorpus);
    
    // Calculate required SIP to meet the goal
    const FV = requiredCorpus - currentSavings * Math.pow(1 + monthlyRate, months);
    const requiredSIP = FV > 0 ? FV * monthlyRate / (Math.pow(1 + monthlyRate, months) - 1) : 0;
    const additionalSIPNeeded = Math.max(0, requiredSIP - monthlySIP);

    return {
      requiredCorpus: Math.round(requiredCorpus),
      accumulatedCorpus: Math.round(accumulatedCorpus),
      totalInvestment: Math.round(totalInvestment),
      shortfall: Math.round(shortfall),
      requiredSIP: Math.round(requiredSIP),
      additionalSIPNeeded: Math.round(additionalSIPNeeded),
      expenseAtRetirement: Math.round(expenseAtRetirement),
      yearlyData,
      yearsToRetirement,
    };
  }, [currentAge, retirementAge, lifeExpectancy, monthlyExpense, inflationRate, expectedReturn, expectedReturnPostRetirement, currentSavings, monthlySIP, enableStepUp, stepUpType, stepUpValue]);

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    return `₹${value.toLocaleString('en-IN')}`;
  };

  const isOnTrack = results.shortfall === 0;
  const visibleYearRows = results.yearlyData.slice(0, Math.min(tableVisibleRows, results.yearlyData.length));

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-text-primary mb-4">
            Retirement Calculator
          </h1>
          <p className="text-text-secondary text-lg max-w-2xl mx-auto">
            Plan your retirement corpus and see if your current investments are on track
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="glass-effect rounded-2xl p-8">
            <h2 className="font-heading text-2xl font-bold text-text-primary mb-6">Your Details</h2>
            
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-text-secondary text-sm mb-2">Current Age</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={currentAgeInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setCurrentAgeInput(raw);
                      if (raw.trim() === '') return;
                      const n = parseInt(raw, 10);
                      if (!Number.isFinite(n)) return;
                      const next = Math.max(18, Math.min(retirementAge - 1, n));
                      setCurrentAge(next);
                    }}
                    onBlur={() => {
                      const n = parseInt(currentAgeInput.trim(), 10);
                      const next = Number.isFinite(n) ? Math.max(18, Math.min(retirementAge - 1, n)) : currentAge;
                      setCurrentAge(next);
                      setCurrentAgeInput(String(next));
                      if (retirementAge <= next) {
                        const adjusted = Math.max(next + 1, retirementAge);
                        setRetirementAge(adjusted);
                        setRetirementAgeInput(String(adjusted));
                      }
                    }}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                    min="18"
                    data-testid="ret-current-age"
                  />
                </div>
                <div>
                  <label className="block text-text-secondary text-sm mb-2">Retirement Age</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={retirementAgeInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setRetirementAgeInput(raw);
                      if (raw.trim() === '') return;
                      const n = parseInt(raw, 10);
                      if (!Number.isFinite(n)) return;
                      const maxAllowed = Math.min(80, lifeExpectancy - 1);
                      const next = Math.max(currentAge + 1, Math.min(maxAllowed, n));
                      setRetirementAge(next);
                    }}
                    onBlur={() => {
                      const n = parseInt(retirementAgeInput.trim(), 10);
                      const maxAllowed = Math.min(80, lifeExpectancy - 1);
                      const next = Number.isFinite(n) ? Math.max(currentAge + 1, Math.min(maxAllowed, n)) : retirementAge;
                      setRetirementAge(next);
                      setRetirementAgeInput(String(next));
                      if (lifeExpectancy <= next) {
                        const adjusted = Math.max(next + 1, lifeExpectancy);
                        setLifeExpectancy(adjusted);
                        setLifeExpectancyInput(String(adjusted));
                      }
                    }}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                    data-testid="ret-retirement-age"
                  />
                </div>
                <div>
                  <label className="block text-text-secondary text-sm mb-2">Life Expectancy</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={lifeExpectancyInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setLifeExpectancyInput(raw);
                      if (raw.trim() === '') return;
                      const n = parseInt(raw, 10);
                      if (!Number.isFinite(n)) return;
                      const next = Math.max(retirementAge + 1, Math.min(110, n));
                      setLifeExpectancy(next);
                    }}
                    onBlur={() => {
                      const n = parseInt(lifeExpectancyInput.trim(), 10);
                      const next = Number.isFinite(n) ? Math.max(retirementAge + 1, Math.min(110, n)) : lifeExpectancy;
                      setLifeExpectancy(next);
                      setLifeExpectancyInput(String(next));
                    }}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                    data-testid="ret-life-expectancy"
                  />
                </div>
              </div>

              <div>
                <label className="block text-text-secondary text-sm mb-2">Current Monthly Expense (₹)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={monthlyExpenseInput}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setMonthlyExpenseInput(raw);
                    if (raw.trim() === '') return;
                    const n = parseInt(raw.replace(/,/g, ''), 10);
                    if (!Number.isFinite(n)) return;
                    setMonthlyExpense(Math.max(10000, n));
                  }}
                  onFocus={() => setMonthlyExpenseInput(String(monthlyExpense))}
                  onBlur={() => {
                    const n = parseInt(monthlyExpenseInput.replace(/,/g, '').trim(), 10);
                    const next = Number.isFinite(n) ? Math.max(10000, n) : monthlyExpense;
                    setMonthlyExpense(next);
                    setMonthlyExpenseInput(formatIndianNumber(next));
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  data-testid="ret-monthly-expense"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-text-secondary text-sm mb-2">Inflation Rate (%)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={inflationRateInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setInflationRateInput(raw);
                      if (raw.trim() === '') return;
                      const n = parseNumber(raw);
                      if (!Number.isFinite(n)) return;
                      if (n >= 2 && n <= 15) setInflationRate(n);
                    }}
                    onBlur={() => {
                      if (inflationRateInput.trim() === '') {
                        setInflationRateInput(String(inflationRate));
                        return;
                      }
                      const n = parseNumber(inflationRateInput);
                      const next = Number.isFinite(n) ? Math.max(2, Math.min(15, n)) : inflationRate;
                      setInflationRate(next);
                      setInflationRateInput(String(next));
                    }}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                    step="0.5"
                    data-testid="ret-inflation"
                  />
                </div>
                <div>
                  <label className="block text-text-secondary text-sm mb-2">Expected Return (%) - 2-12%</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={expectedReturnInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setExpectedReturnInput(raw);
                      if (raw.trim() === '') return;
                      const n = parseNumber(raw);
                      if (!Number.isFinite(n)) return;
                      if (n >= 2 && n <= 12) setExpectedReturn(n);
                    }}
                    onBlur={() => {
                      if (expectedReturnInput.trim() === '') {
                        setExpectedReturnInput(String(expectedReturn));
                        return;
                      }
                      const n = parseNumber(expectedReturnInput);
                      const next = Number.isFinite(n) ? Math.max(2, Math.min(12, n)) : expectedReturn;
                      setExpectedReturn(next);
                      setExpectedReturnInput(String(next));
                    }}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                    min="2"
                    max="12"
                    step="0.5"
                    data-testid="ret-expected-return"
                  />
                </div>
                <div>
                  <label className="block text-text-secondary text-sm mb-2">Expected Return (post retirement) (%) - 2-12%</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={expectedReturnPostRetirementInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setExpectedReturnPostRetirementInput(raw);
                      if (raw.trim() === '') return;
                      const n = parseNumber(raw);
                      if (!Number.isFinite(n)) return;
                      if (n >= 2 && n <= 12) setExpectedReturnPostRetirement(n);
                    }}
                    onBlur={() => {
                      if (expectedReturnPostRetirementInput.trim() === '') {
                        setExpectedReturnPostRetirementInput(String(expectedReturnPostRetirement));
                        return;
                      }
                      const n = parseNumber(expectedReturnPostRetirementInput);
                      const next = Number.isFinite(n) ? Math.max(2, Math.min(12, n)) : expectedReturnPostRetirement;
                      setExpectedReturnPostRetirement(next);
                      setExpectedReturnPostRetirementInput(String(next));
                    }}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                    min="2"
                    max="12"
                    step="0.5"
                    data-testid="ret-expected-return-post"
                  />
                </div>
              </div>

              <div>
                <label className="block text-text-secondary text-sm mb-2">Current Savings (₹)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={currentSavingsInput}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setCurrentSavingsInput(raw);
                    if (raw.trim() === '') return;
                    const n = parseInt(raw.replace(/,/g, ''), 10);
                    if (!Number.isFinite(n)) return;
                    setCurrentSavings(Math.max(0, n));
                  }}
                  onFocus={() => setCurrentSavingsInput(String(currentSavings))}
                  onBlur={() => {
                    const n = parseInt(currentSavingsInput.replace(/,/g, '').trim(), 10);
                    const next = Number.isFinite(n) ? Math.max(0, n) : currentSavings;
                    setCurrentSavings(next);
                    setCurrentSavingsInput(formatIndianNumber(next));
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  data-testid="ret-current-savings"
                />
              </div>

              <div>
                <label className="block text-text-secondary text-sm mb-2">Monthly SIP (₹)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={monthlySIPInput}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setMonthlySIPInput(raw);
                    if (raw.trim() === '') return;
                    const n = parseInt(raw.replace(/,/g, ''), 10);
                    if (!Number.isFinite(n)) return;
                    setMonthlySIP(Math.max(500, n));
                  }}
                  onFocus={() => setMonthlySIPInput(String(monthlySIP))}
                  onBlur={() => {
                    const n = parseInt(monthlySIPInput.replace(/,/g, '').trim(), 10);
                    const next = Number.isFinite(n) ? Math.max(500, n) : monthlySIP;
                    setMonthlySIP(next);
                    setMonthlySIPInput(formatIndianNumber(next));
                  }}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  data-testid="ret-monthly-sip"
                />
              </div>

              {/* Step-Up SIP */}
              <div className="border-t border-white/10 pt-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableStepUp}
                    onChange={(e) => setEnableStepUp(e.target.checked)}
                    className="w-5 h-5 rounded border-white/10 bg-white/5 text-primary focus:ring-primary"
                  />
                  <span className="ml-3 text-text-primary font-medium">Enable Step-Up SIP</span>
                </label>

                {enableStepUp && (
                  <div className="mt-4 space-y-3 pl-8">
                    <div className="flex gap-4 flex-wrap">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="retStepUpType"
                          value="percentage"
                          checked={stepUpType === 'percentage'}
                          onChange={() => {
                            setStepUpType('percentage');
                            setStepUpValueInput(String(stepUpValue));
                          }}
                          className="w-4 h-4 text-primary"
                        />
                        <span className="ml-2 text-text-secondary text-sm">% annually</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="retStepUpType"
                          value="fixed"
                          checked={stepUpType === 'fixed'}
                          onChange={() => {
                            setStepUpType('fixed');
                            setStepUpValueInput(formatIndianNumber(stepUpValue));
                          }}
                          className="w-4 h-4 text-primary"
                        />
                        <span className="ml-2 text-text-secondary text-sm">Fixed amount</span>
                      </label>
                    </div>
                    <input
                      type="text"
                      inputMode={stepUpType === 'percentage' ? 'decimal' : 'numeric'}
                      value={stepUpValueInput}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setStepUpValueInput(raw);
                        if (raw.trim() === '') return;
                        const n = parseNumber(raw);
                        if (!Number.isFinite(n)) return;
                        setStepUpValue(Math.max(0, n));
                      }}
                      onFocus={() => setStepUpValueInput(String(stepUpValue))}
                      onBlur={() => {
                        if (stepUpValueInput.trim() === '') {
                          setStepUpValueInput(stepUpType === 'fixed' ? formatIndianNumber(stepUpValue) : String(stepUpValue));
                          return;
                        }
                        const n = parseNumber(stepUpValueInput);
                        const next = Number.isFinite(n) ? Math.max(0, n) : stepUpValue;
                        setStepUpValue(next);
                        setStepUpValueInput(stepUpType === 'fixed' ? formatIndianNumber(next) : String(next));
                      }}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                      placeholder={stepUpType === 'percentage' ? 'Annual % increase' : 'Annual ₹ increase'}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-6">
            {/* Status Banner */}
            <div className={`glass-effect rounded-xl p-4 border-l-4 ${isOnTrack ? 'border-green-400' : 'border-red-400'}`}>
              <div className="flex items-center gap-3">
                {isOnTrack ? (
                  <Target className="h-6 w-6 text-green-400" />
                ) : (
                  <AlertTriangle className="h-6 w-6 text-red-400" />
                )}
                <p className={`font-semibold ${isOnTrack ? 'text-green-400' : 'text-red-400'}`}>
                  {isOnTrack ? "You're on track for retirement!" : `Shortfall of ${formatCurrency(results.shortfall)}`}
                </p>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="glass-effect rounded-xl p-5 text-center">
                <Target className="h-7 w-7 text-primary mx-auto mb-2" />
                <p className="text-text-muted text-xs">Required Corpus</p>
                <p className="text-xl font-bold text-text-primary">{formatCurrency(results.requiredCorpus)}</p>
              </div>
              <div className="glass-effect rounded-xl p-5 text-center">
                <PiggyBank className="h-7 w-7 text-secondary mx-auto mb-2" />
                <p className="text-text-muted text-xs">Projected Corpus</p>
                <p className="text-xl font-bold text-secondary">{formatCurrency(results.accumulatedCorpus)}</p>
              </div>
              <div className="glass-effect rounded-xl p-5 text-center">
                <Wallet className="h-7 w-7 text-blue-400 mx-auto mb-2" />
                <p className="text-text-muted text-xs">Monthly Expense at Retirement</p>
                <p className="text-xl font-bold text-blue-400">{formatCurrency(results.expenseAtRetirement)}</p>
              </div>
              <div className="glass-effect rounded-xl p-5 text-center">
                <TrendingUp className="h-7 w-7 text-green-400 mx-auto mb-2" />
                <p className="text-text-muted text-xs">Required Monthly SIP</p>
                <p className="text-xl font-bold text-green-400">{formatCurrency(results.requiredSIP)}</p>
              </div>
            </div>

            {/* Insight */}
            {!isOnTrack && (
              <div className="glass-effect rounded-xl p-4 border-l-4 border-primary">
                <p className="text-text-secondary">
                  <span className="text-primary font-semibold">Insight:</span> You are short by <span className="text-red-400 font-bold">{formatCurrency(results.shortfall)}</span>. Increase your SIP by <span className="text-green-400 font-bold">{formatCurrency(results.additionalSIPNeeded)}/month</span> to bridge the gap.
                </p>
              </div>
            )}

            {/* Chart */}
            <div className="glass-effect rounded-2xl p-6">
              <h3 className="font-heading text-xl font-bold text-text-primary mb-4">Corpus Growth vs Required</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={results.yearlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis dataKey="year" stroke="#94a3b8" fontSize={11} />
                    <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `₹${(v/10000000).toFixed(1)}Cr`} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <ReferenceLine y={results.requiredCorpus} stroke="#ef4444" strokeDasharray="5 5" label={{ value: 'Target', fill: '#ef4444', fontSize: 11 }} />
                    <Line type="monotone" dataKey="corpus" name="Your Corpus" stroke="#F5A623" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Year-wise Table */}
        <div className="mt-8 glass-effect rounded-2xl p-6">
          <h3 className="font-heading text-xl font-bold text-text-primary mb-4">Year-wise Accumulation</h3>
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="sticky top-20 bg-background z-10 text-left py-3 px-4 text-text-muted font-medium">Age</th>
                <th className="sticky top-20 bg-background z-10 text-right py-3 px-4 text-text-muted font-medium">Total Investment</th>
                <th className="sticky top-20 bg-background z-10 text-right py-3 px-4 text-text-muted font-medium">Accumulated Corpus</th>
                <th className="sticky top-20 bg-background z-10 text-right py-3 px-4 text-text-muted font-medium">Utilised Amount</th>
                <th className="sticky top-20 bg-background z-10 text-right py-3 px-4 text-text-muted font-medium">Remaining Corpus</th>
              </tr>
            </thead>
            <tbody>
              {visibleYearRows.map((row, index) => (
                <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                  <td className="py-3 px-4 text-text-primary">{row.year}</td>
                  <td className="py-3 px-4 text-right text-text-secondary">{formatCurrency(row.investment)}</td>
                  <td className="py-3 px-4 text-right text-primary font-medium">{formatCurrency(row.corpus)}</td>
                  <td className="py-3 px-4 text-right text-text-secondary">{formatCurrency(row.utilisedAmount || 0)}</td>
                  <td className="py-3 px-4 text-right text-secondary font-medium">{formatCurrency(row.remainingCorpus ?? row.corpus)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {results.yearlyData.length > 15 && (
            <div className="mt-4 flex items-center justify-center gap-3">
              {tableVisibleRows < results.yearlyData.length && (
                <Button
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary/10"
                  onClick={() => setTableVisibleRows((n) => Math.min(results.yearlyData.length, n + 15))}
                >
                  Show More
                </Button>
              )}
              {tableVisibleRows > 15 && (
                <Button
                  variant="outline"
                  className="border-white/10 text-text-secondary hover:bg-white/5"
                  onClick={() => setTableVisibleRows((n) => Math.max(15, n - 15))}
                >
                  Show Less
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <div className="mt-8 glass-effect rounded-xl p-4 border border-yellow-500/30">
          <p className="text-text-muted text-sm">
            <span className="text-yellow-500 font-semibold">Disclaimer:</span> The calculations shown are based on the inputs provided and assume constant returns and inflation throughout the period. Actual returns may vary based on market conditions, fund performance, and other factors. These projections are for illustrative purposes only and should not be considered as financial advice. Please consult a qualified financial advisor before making investment decisions.
          </p>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <div className="glass-effect rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="font-heading text-2xl font-bold text-text-primary mb-4">Get a Personalized Investment Plan</h3>
            <p className="text-text-secondary mb-6">Let us help you create a customized retirement strategy based on your goals</p>
            <Link to="/contact">
              <Button className="btn-primary text-lg px-8 py-4">
                Get Started <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
